import { createFileRoute } from '@tanstack/react-router';
import { Grid, Stack, Typography, styleVars } from 'gooey';
import _ from 'lodash';
import { motion } from 'motion/react';
import React from 'react';
import { InView } from 'react-intersection-observer';
import { match } from 'ts-pattern';
import BiDiagram3 from '~icons/bi/diagram-3';
import CarbonApi from '~icons/carbon/api';
import FluentChatSparkle16Filled from '~icons/fluent/chat-sparkle-16-filled';
import FluentDocumentOnePageSparkle24Filled from '~icons/fluent/document-one-page-sparkle-24-filled';
import FluentFlashSparkle16Filled from '~icons/fluent/flash-sparkle-16-filled';
import FluentPenSparkle16Filled from '~icons/fluent/pen-sparkle-16-filled';
import GardenBotSparkleStroke12 from '~icons/garden/bot-sparkle-stroke-12';
import LucideWandSparkles from '~icons/lucide/wand-sparkles';
import MajesticonsCodeBlock from '~icons/majesticons/code-block';
import TablerIconBrain from '~icons/tabler/brain';
import TablerIconBrandGithub from '~icons/tabler/brand-github';
import TablerIconBrandLoom from '~icons/tabler/brand-loom';
import TablerIconChevronRight from '~icons/tabler/chevron-right';
import TablerIconMarkdown from '~icons/tabler/markdown';
import TablerIconMessage from '~icons/tabler/message';
import TablerIconPackage from '~icons/tabler/package';
import { DemoAssistant, DemoGenerate, DemoMaintain } from '#/components/demos';
import { Img } from '#/components/images';
import { ButtonLink } from '#/components/links';
import { Page, PageSectionTitle } from '#/components/pages';
import { PageSection } from '#/components/pages/page-section';
import { CustomerLogo, CustomerTestimonialCard } from '#/components/widgets';
import fx from '#/fx';
import { Content, type ExtractLoaderData, pageSEO } from '#/lib/content';
import { applyLogoConfigs } from '#/lib/customers';
import { canonicalLinkTag } from '#/lib/head';

export const Route = createFileRoute('/_main/')({
  loader: async () => {
    return await load();
  },
  head: ({ loaderData, match }) => ({
    meta: pageSEO(
      {
        title: 'Home',
        description: 'Joggr is the AI documentation platform built for developers.',
        keywords: ['docs', 'ai', 'documentation', 'developer', 'platform'],
      },
      match,
      loaderData?.page
    ),
    links: [canonicalLinkTag(match.pathname)],
  }),
  onCatch(error) {
    console.error(error, 'Unable to capture exception');
  },
  component: HomePage,
});

async function load() {
  const page = await fx.getPageContent({ data: { key: 'home' } });
  const testimonials = await fx.listCustomerTestimonials({
    data: {
      fields: {
        ids: page.sections.testimonials.testimonials,
      },
    },
  });
  const customerLogos = await fx.listCustomerLogos({
    data: {
      fields: {
        ids: page.sections.customers.logos,
      },
    },
  });

  return {
    page: {
      ...page,
      sections: {
        ...page.sections,
        testimonials: {
          ...page.sections.testimonials,
          testimonials,
        },
        customers: {
          ...page.sections.customers,
          logos: customerLogos,
        },
      },
    },
  };
}

type LoaderData = ExtractLoaderData<typeof load>;

function HomePage() {
  const { page } = Route.useLoaderData();
  const sections = React.useMemo(() => page.sections, [page.sections]);
  return (
    <Page className={'min-h-screen overflow-hidden relative pb-36'} gutter={0}>
      <PageSectionHeroSection sections={sections} />
      <PageSectionCustomerHighlightSection sections={sections} />
      <PageSectionMaintain sections={sections} />
      <PageSectionAssistant sections={sections} />
      <PageSectionGenerate sections={sections} />
      <PageSectionFeatures sections={sections} />
      <PageSectionTestimonials sections={sections} />
    </Page>
  );
}

/*
|------------------
| Sections
|------------------
*/

const MotionImg = motion.create(Img);

function PageSectionHeroSection({ sections }: HomePageSectionProps) {
  return (
    <PageSection className='relative mt-4 mb-12' disableGutters>
      <MotionImg
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          duration: 0.33,
          delay: 0,
          ease: 'easeIn',
        }}
        location='static'
        src={sections.hero.images.overlay.src}
        alt='Overlay image of slack messages where the user is chatting with the Joggr AI assistant and asking for a Tech Spec document. The AI replies in Slack and then creates a PR in GitHub.'
        width={sections.hero.images.overlay.width}
        height={sections.hero.images.overlay.height}
        className='absolute bottom-12 left-2 w-1/3 md:w-[400px] lg:w-[525px] h-auto max-w-none z-100 drop-shadow-(--hero-overlay-drop-shadow)'
        style={styleVars({
          '--hero-overlay-drop-shadow': '12px -12px 24px rgb(16 16 16 / 0.9)',
        })}
      />
      <div className='relative px-4 pt-12 pb-8 border border-gray-900 rounded-2xl overflow-hidden bg-transparent'>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            duration: 0.33,
            delay: 0,
            ease: 'easeIn',
          }}
          className='z-0 brightness-15 absolute inset-0'
        >
          <img
            fetchPriority='low'
            loading='lazy'
            draggable={false}
            decoding='async'
            src='/images/backgrounds/grid-white.png'
            alt='background-image'
            className='h-auto max-w-none min-h-full w-[138%] object-cover select-none'
          />
        </motion.div>
        <Stack className='max-w-[1200px] my-auto z-10 relative' spacing={3}>
          <Typography
            variant='h1'
            className='sm:text-6xl md:text-6xl lg:text-8xl font-bold text-center'
          >
            <Content content={sections.hero.title} />
          </Typography>
          <Typography variant='h6' component='p' className='md:text-xl text-center'>
            <Content content={sections.hero.subtitle} />
          </Typography>
          <Stack direction='row' spacing={2} className='text-center mx-auto w-fit'>
            {(
              [
                { size: 'small', className: 'flex md:hidden' },
                { size: 'large', className: 'hidden md:flex' },
              ] as const
            ).map((cta) => (
              <React.Fragment key={cta.size}>
                <ButtonLink
                  variant='contained'
                  size={cta.size}
                  className={cta.className}
                  to={sections.hero.cta.primary.url}
                  endIcon={<TablerIconChevronRight />}
                >
                  {sections.hero.cta.primary.label}
                </ButtonLink>
                {sections.hero.cta.secondary && (
                  <ButtonLink
                    variant='outlined'
                    size={cta.size}
                    className={cta.className}
                    to={sections.hero.cta.secondary.url}
                  >
                    {sections.hero.cta.secondary.label}
                  </ButtonLink>
                )}
              </React.Fragment>
            ))}
          </Stack>
          <Img
            location='static'
            src={sections.hero.images.main.src}
            alt='A screenshot of the Joggr dashboard where the user is editing a Tech Spec document, that was created by the Joggr AI assistant.'
            width={sections.hero.images.main.width}
            height={sections.hero.images.main.height}
            className='w-full h-auto max-w-none drop-shadow-(--hero-main-drop-shadow)'
            style={styleVars({
              '--hero-main-drop-shadow': '0 8px 12px rgb(20 20 20 / 0.7)',
            })}
          />
        </Stack>
      </div>
    </PageSection>
  );
}

function PageSectionCustomerHighlightSection({ sections }: HomePageSectionProps) {
  const customers = React.useMemo(() => {
    return applyLogoConfigs(sections.customers.logos);
  }, [sections.customers.logos]);

  return (
    <PageSection className='bg-transparent text-center pt-12 pb-4' gutterTop={0}>
      <Typography variant='h6' component='p' className='mb-16 text-gray-300'>
        <Content content={sections.customers.title} />
      </Typography>
      <div className='grid grid-cols-2 md:grid-cols-3 gap-12'>
        {customers.map((customer) => (
          <div key={customer.id}>
            <CustomerLogo
              customerLogo={customer}
              height={customer.logoHeight}
              className='mx-auto opacity-70 max-w-full'
            />
          </div>
        ))}
      </div>
    </PageSection>
  );
}

function PageSectionMaintain({ sections }: HomePageSectionProps) {
  return (
    <PageSection>
      <PageSectionTitle
        icon={<LucideWandSparkles />}
        iconAnimation='wiggle'
        title={sections.maintain.title}
        subtitle={sections.maintain.subtitle}
      />
      <InView threshold={0.85}>
        {({ entry, ref }) => {
          return (
            <div ref={ref}>
              <DemoMaintain disabled={!entry?.isIntersecting} />
            </div>
          );
        }}
      </InView>
    </PageSection>
  );
}

function PageSectionAssistant({ sections }: HomePageSectionProps) {
  return (
    <PageSection>
      <PageSectionTitle
        icon={<GardenBotSparkleStroke12 />}
        iconAnimation='shine'
        title={sections.assistant.title}
        subtitle={sections.assistant.subtitle}
      />
      <InView threshold={0.85}>
        {({ entry, ref }) => {
          return (
            <div ref={ref}>
              <DemoAssistant disabled={!entry?.isIntersecting} />
            </div>
          );
        }}
      </InView>
    </PageSection>
  );
}

function PageSectionGenerate({ sections }: HomePageSectionProps) {
  return (
    <PageSection>
      <PageSectionTitle
        icon={<FluentDocumentOnePageSparkle24Filled />}
        iconAnimation='shine'
        title={sections.generate.title}
        subtitle={sections.generate.subtitle}
      />
      <InView threshold={0.85}>
        {({ entry, ref }) => {
          return (
            <div ref={ref}>
              <DemoGenerate disabled={!entry?.isIntersecting} />
            </div>
          );
        }}
      </InView>
    </PageSection>
  );
}

function PageSectionFeatures({ sections }: HomePageSectionProps) {
  return (
    <PageSection>
      <Stack className='mb-16'>
        <Typography variant='h2' component='h2' gutterBottom>
          <Content content={sections.features.title} />
        </Typography>
        <Typography variant='subtitle1'>
          <Content content={sections.features.subtitle} />
        </Typography>
      </Stack>
      <Grid container spacing={3}>
        {sections.features.list.map((feature) => (
          <Grid
            key={feature.title}
            className='md:min-h-[164px] justify-stretch'
            size={{ xs: 12, sm: 6, md: 4 }}
          >
            <Stack spacing={1}>
              <div className='[&_.icon]:size-8'>{getIcon(feature.icon)}</div>
              <Typography variant='h6' component='h4' className='md:text-xl'>
                <Content content={feature.title} />
              </Typography>
              <Typography variant='body2'>
                <Content content={feature.description} />
              </Typography>
            </Stack>
          </Grid>
        ))}
      </Grid>
      <ButtonLink
        to={sections.features.cta.url}
        endIcon={<TablerIconChevronRight />}
        variant='contained'
        size='large'
        className='mx-auto flex mt-12 w-fit'
      >
        {sections.features.cta.label}
      </ButtonLink>
    </PageSection>
  );
}

function PageSectionTestimonials({ sections }: HomePageSectionProps) {
  const { title, subtitle } = sections.testimonials;
  const [featuredTestimonial, ...testimonials] = sections.testimonials.testimonials;
  return (
    <PageSection id='testimonials'>
      <PageSectionTitle title={title} subtitle={subtitle} />
      <Grid container spacing={{ xs: 2, sm: 4 }} rowSpacing={{ xs: 6 }} className='justify-stretch'>
        <Grid size={12}>
          <CustomerTestimonialCard testimonial={featuredTestimonial} size='large' />
        </Grid>
        {testimonials.map((testimonial) => (
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 6,
            }}
            key={testimonial.id}
          >
            <CustomerTestimonialCard testimonial={testimonial} />
          </Grid>
        ))}
      </Grid>
      <Stack
        direction='row'
        spacing={2}
        alignItems='center'
        justifyContent='center'
        className='mt-8 md:mt-16 w-full'
      >
        <ButtonLink
          to={sections.testimonials.cta.url}
          variant='contained'
          size='large'
          endIcon={<TablerIconChevronRight />}
          className='mx-auto w-full xs:w-fit'
        >
          {sections.testimonials.cta.label}
        </ButtonLink>
      </Stack>
    </PageSection>
  );
}

/*
|------------------
| Internals
|------------------
*/

type HomePageSectionProps = Pick<LoaderData['page'], 'sections'>;

function getIcon(icon: string) {
  return match(icon)
    .with('diagramming', () => <BiDiagram3 />)
    .with('markdown', () => <TablerIconMarkdown />)
    .with('message', () => <TablerIconMessage />)
    .with('codeblock', () => <MajesticonsCodeBlock />)
    .with('brain', () => <TablerIconBrain />)
    .with('package', () => <TablerIconPackage />)
    .with('api', () => <CarbonApi />)
    .with('github', () => <TablerIconBrandGithub />)
    .with('flash-sparkles', () => <FluentFlashSparkle16Filled />)
    .with('chat-sparkle', () => <FluentChatSparkle16Filled />)
    .with('pen-sparkle', () => <FluentPenSparkle16Filled />)
    .with('loom', () => <TablerIconBrandLoom />)
    .otherwise(() => null);
}
